
import { Component, Prop, Vue } from 'vue-property-decorator';

import GoogleMaps from '../../anonymous/GoogleMaps.vue';

import { sharedState } from '../../../../framework/state';
import { getResource } from '../../../../framework/client/resource';
import { getEnumerationOptions, getGroupOptions, getOptionLabel } from '../../../../framework/service/options';

import { CustomerType } from '../../../../../common/application/model/enums/CustomerType';
import { Customer } from '../../../../../common/application/model/Customer';
import { CustomerState } from '../../../../../common/application/model/enums/CustomerState';
import { InvoiceType } from '../../../../../common/application/model/enums/InvoiceType';
import { PaymentTerms } from '../../../../../common/application/model/enums/PaymentTerms';
import { getCustomerOptions } from '../../../service/application_options_service';
import { Iso4217CurrencyCode } from '../../../../../common/application/model/enums/Iso4217CurrencyCode';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import ViewDocuments from '../document/ViewDocuments.vue';
import AddDocument from '../document/AddDocument.vue';
import { ApplicationUserRole } from '../../../../../common/application/enumeration/ApplicationUserRole';

@Component({
    components: {
        GoogleMaps,
        ViewDocuments,
        AddDocument,
    },
    beforeRouteEnter(to: any, from: any, next: any) {
        next((vm: ViewCustomer) => {
            vm.getPrevRouter(from); //get the previous URL
        });
    },
})
export default class ViewCustomer extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.CUSTOMER;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as Customer,
        customerTypeOptions: [] as { id: string | undefined; label: string }[],
        customerStateOptions: [] as { id: string | undefined; label: string }[],
        invoiceTypeOptions: [] as { id: string | undefined; label: string }[],
        paymentTermsOptions: [] as { id: string | undefined; label: string }[],
        groupOptions: [] as { id: string | undefined; label: string }[],
        customerOptions: [] as { id: string | undefined; label: string }[],
        currencyOptions: [] as { id: string | undefined; label: string }[],
        customerType: '',
        customerState: '',
        invoiceType: '',
        paymentTerms: '',
        managerGroup: '',
        parentCustomer: '',
        activeTab: 0,
        prevRoute: '',
        showAddDocument: false,
    };

    // Functions
    async mounted() {
        this.local.activeTab = Number(localStorage.getItem(`${this.resourceType}_ActiveTab`));
        this.local.customerTypeOptions = getEnumerationOptions('CustomerType', Object.keys(CustomerType), this);
        this.local.customerStateOptions = getEnumerationOptions('CustomerState', Object.keys(CustomerState), this);
        this.local.invoiceTypeOptions = getEnumerationOptions('InvoiceType', Object.keys(InvoiceType), this);
        this.local.paymentTermsOptions = getEnumerationOptions('PaymentTerms', Object.keys(PaymentTerms), this);
        this.local.customerOptions = await getCustomerOptions();
        this.local.currencyOptions = getEnumerationOptions(
            'Iso4217CurrencyCode',
            Object.keys(Iso4217CurrencyCode),
            this,
        );
        this.local.row = (await getResource<Customer>(this.resourceType, this.id))!!;
        this.local.customerOptions = this.local.customerOptions.filter((o) => o.id !== this.local.row.id);

        this.local.customerType = getOptionLabel(this.local.row.customerType || '', this.local.customerTypeOptions);
        this.local.customerState = getOptionLabel(this.local.row.customerState || '', this.local.customerStateOptions);
        this.local.invoiceType = getOptionLabel(this.local.row.invoiceType || '', this.local.invoiceTypeOptions);
        this.local.paymentTerms = getOptionLabel(this.local.row.paymentTerms || '', this.local.paymentTermsOptions);
        this.local.parentCustomer = getOptionLabel(this.local.row.parentId || '', this.local.customerOptions);

        if (this.shared.admin) {
            this.local.groupOptions = await getGroupOptions();
            this.local.managerGroup = getOptionLabel(this.local.row.managerGroupId || '', this.local.groupOptions);
        }
        localStorage.removeItem(`${this.resourceType}_ActiveTab`);
    }

    back() {
        this.$router.go(-1);
    }

    edit() {
        this.$router.push(`/${this.resourceType}/${this.local.row.id}/edit`);
    }

    addDocument() {
        this.$router.push({ name: 'EditCustomer', params: { activeTab: this.local.activeTab } } as any);
        if (this.isManager) {
            this.local.showAddDocument = true;
        } else if (this.isAdmin) {
            this.$router.push({ name: 'EditCustomer', params: { activeTab: this.local.activeTab } } as any);
        }
    }

    get coords(): google.maps.LatLngLiteral {
        return {
            lat: this.local.row.lat,
            lng: this.local.row.lng,
        };
    }

    get fullAddress(): string {
        if (!this.local.row.invoicingAddressStreet) {
            return '';
        }

        return `${this.local.row.invoicingAddressStreet}, ${this.local.row.invoicingAddressZip} ${this.local.row.invoicingAddressCity}`;
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    getPrevRouter(from: any) {
        this.local.prevRoute = from.path;
        if (this.local.prevRoute.includes('/file-meta/')) {
            this.local.activeTab = 1;
        }
    }

    getActiveTab(value: number) {
        localStorage.setItem(`${this.resourceType}_ActiveTab`, JSON.stringify(value));
    }
}
